// Validation utilities for form inputs

export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!email) return { isValid: false, message: "Email is required" };
  if (!emailRegex.test(email)) return { isValid: false, message: "Please enter a valid email address" };
  if (email.length > 254) return { isValid: false, message: "Email is too long" };
  return { isValid: true, message: "" };
};

export const validateUsername = (username) => {
  if (!username) return { isValid: false, message: "Username is required" };
  if (username.length < 3) return { isValid: false, message: "Username must be at least 3 characters" };
  if (username.length > 30) return { isValid: false, message: "Username must be less than 30 characters" };
  if (!/^[a-zA-Z0-9_-]+$/.test(username)) return { isValid: false, message: "Username can only contain letters, numbers, hyphens, and underscores" };
  return { isValid: true, message: "" };
};

export const validatePassword = (password) => {
  if (!password) return { isValid: false, message: "Password is required" };
  if (password.length < 6) return { isValid: false, message: "Password must be at least 6 characters" };
  if (password.length > 128) return { isValid: false, message: "Password is too long" };
  return { isValid: true, message: "" };
};

export const validateGoalTitle = (title) => {
  if (!title) return { isValid: false, message: "Goal is required" };
  if (title.trim().length < 10) return { isValid: false, message: "Goal must be at least 10 characters" };
  if (title.length > 500) return { isValid: false, message: "Goal is too long (max 500 characters)" };
  return { isValid: true, message: "" };
};

export const validateGoalContext = (context) => {
  // Context is optional, but if provided should be meaningful
  if (context && context.trim().length > 0) {
    if (context.length > 1000) return { isValid: false, message: "Context is too long (max 1000 characters)" };
    if (context.trim().length < 5) return { isValid: false, message: "Context should be at least 5 characters if provided" };
  }
  return { isValid: true, message: "" };
};

export const validateExerciseName = (name) => {
  if (!name) return { isValid: false, message: "Exercise name is required" };
  if (name.trim().length < 3) return { isValid: false, message: "Exercise name must be at least 3 characters" };
  if (name.length > 150) return { isValid: false, message: "Exercise name is too long (max 150 characters)" };
  return { isValid: true, message: "" };
};

export const validateExerciseDescription = (description) => {
  if (!description) return { isValid: false, message: "Exercise description is required" };
  if (description.trim().length < 10) return { isValid: false, message: "Description must be at least 10 characters" };
  if (description.length > 1000) return { isValid: false, message: "Description is too long (max 1000 characters)" };
  return { isValid: true, message: "" };
};

export const validateExerciseInstructions = (instructions) => {
  if (!instructions) return { isValid: false, message: "Exercise instructions are required" };
  if (instructions.trim().length < 10) return { isValid: false, message: "Instructions must be at least 10 characters" };
  if (instructions.length > 2000) return { isValid: false, message: "Instructions are too long (max 2000 characters)" };
  return { isValid: true, message: "" };
};

export const validateRequired = (value, fieldName) => {
  if (!value || (typeof value === 'string' && value.trim().length === 0)) {
    return { isValid: false, message: `${fieldName} is required` };
  }
  return { isValid: true, message: "" };
};

export const validateLength = (value, min, max, fieldName) => {
  if (value && value.length < min) {
    return { isValid: false, message: `${fieldName} must be at least ${min} characters` };
  }
  if (value && value.length > max) {
    return { isValid: false, message: `${fieldName} must be less than ${max} characters` };
  }
  return { isValid: true, message: "" };
};

// Content safety validation
export const validateContentSafety = (content) => {
  if (!content) return { isValid: true, message: "" };
  
  const inappropriatePatterns = [
    /\b(fuck|shit|damn|hell|bitch|asshole|crap)\b/i,
    /\b(suicide|kill yourself|harm yourself|self-harm)\b/i,
    /\b(illegal|drugs|violence|weapon)\b/i
  ];
  
  for (const pattern of inappropriatePatterns) {
    if (pattern.test(content)) {
      return { isValid: false, message: "Content contains inappropriate language" };
    }
  }
  
  return { isValid: true, message: "" };
};

// Comprehensive form validation
export const validateGoalForm = (goalDetails) => {
  const errors = {};
  
  const goalValidation = validateGoalTitle(goalDetails.goal);
  if (!goalValidation.isValid) errors.goal = goalValidation.message;
  
  const contextValidation = validateGoalContext(goalDetails.context);
  if (!contextValidation.isValid) errors.context = contextValidation.message;
  
  const timeValidation = validateRequired(goalDetails.time, "Time commitment");
  if (!timeValidation.isValid) errors.time = timeValidation.message;
  
  const practiceContextValidation = validateRequired(goalDetails.practiceContext, "Practice context");
  if (!practiceContextValidation.isValid) errors.practiceContext = practiceContextValidation.message;
  
  // Content safety checks
  const goalSafetyValidation = validateContentSafety(goalDetails.goal);
  if (!goalSafetyValidation.isValid) errors.goal = goalSafetyValidation.message;
  
  const contextSafetyValidation = validateContentSafety(goalDetails.context);
  if (!contextSafetyValidation.isValid) errors.context = contextSafetyValidation.message;
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

export const validateExerciseForm = (exerciseDetails) => {
  const errors = {};
  
  const nameValidation = validateExerciseName(exerciseDetails.name);
  if (!nameValidation.isValid) errors.name = nameValidation.message;
  
  const descriptionValidation = validateExerciseDescription(exerciseDetails.description);
  if (!descriptionValidation.isValid) errors.description = descriptionValidation.message;
  
  const instructionsValidation = validateExerciseInstructions(exerciseDetails.instructions);
  if (!instructionsValidation.isValid) errors.instructions = instructionsValidation.message;
  
  // Content safety checks
  const nameSafetyValidation = validateContentSafety(exerciseDetails.name);
  if (!nameSafetyValidation.isValid) errors.name = nameSafetyValidation.message;
  
  const descriptionSafetyValidation = validateContentSafety(exerciseDetails.description);
  if (!descriptionSafetyValidation.isValid) errors.description = descriptionSafetyValidation.message;
  
  const instructionsSafetyValidation = validateContentSafety(exerciseDetails.instructions);
  if (!instructionsSafetyValidation.isValid) errors.instructions = instructionsSafetyValidation.message;
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

export const validateLoginForm = (username, password) => {
  const errors = {};
  
  const usernameValidation = validateUsername(username);
  if (!usernameValidation.isValid) errors.username = usernameValidation.message;
  
  const passwordValidation = validatePassword(password);
  if (!passwordValidation.isValid) errors.password = passwordValidation.message;
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

export const validateRegisterForm = (username, email, password) => {
  const errors = {};
  
  const usernameValidation = validateUsername(username);
  if (!usernameValidation.isValid) errors.username = usernameValidation.message;
  
  const emailValidation = validateEmail(email);
  if (!emailValidation.isValid) errors.email = emailValidation.message;
  
  const passwordValidation = validatePassword(password);
  if (!passwordValidation.isValid) errors.password = passwordValidation.message;
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};